import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';

const { 
  FiLayout, FiEye, FiSave, FiPlus, FiTrash2, FiMove, 
  FiSettings, FiType, FiDroplet, FiCheck, FiMonitor,
  FiSmartphone, FiBox, FiActivity, FiBook, FiMessageCircle,
  FiZap, FiStar, FiRefreshCw
} = FiIcons;

const WIDGET_TEMPLATES = [
  { id: 'welcome', label: 'Welcome Hero', icon: FiType, desc: 'Personalized greeting and daily quote' },
  { id: 'progress', label: 'Progress Matrix', icon: FiActivity, desc: 'Visual course completion metrics' },
  { id: 'resume', label: 'Quick Resume', icon: FiZap, desc: 'Direct link to last watched lesson' },
  { id: 'courses', label: 'Course Catalog', icon: FiBook, desc: 'Grid of enrolled and available courses' },
  { id: 'community', label: 'Feed Preview', icon: FiMessageCircle, desc: 'Latest activity from community' },
  { id: 'achievements', label: 'Badge Vault', icon: FiStar, desc: 'Display earned certificates and badges' }
];

export default function StudentDashboardCustomizer() {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop', 'mobile'
  
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('student_dashboard_configs_2024')
        .select('*')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      if (!data) {
        // Create default if none exists
        const { data: newData, error: insertError } = await supabase
          .from('student_dashboard_configs_2024')
          .insert([{}])
          .select()
          .single();
        if (insertError) throw insertError;
        setConfig(newData);
      } else {
        setConfig(data);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('student_dashboard_configs_2024')
        .update({
          layout_schema: config.layout_schema,
          theme_config: config.theme_config,
          welcome_message: config.welcome_message
        })
        .eq('id', config.id);

      if (error) throw error;
      addToast("Student Experience updated successfully");
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleWidget = (id) => {
    const newLayout = config.layout_schema.map(w => 
      w.id === id ? { ...w, visible: !w.visible } : w
    );
    setConfig({ ...config, layout_schema: newLayout });
  };

  if (loading) return (
    <div className="h-[60vh] flex flex-col items-center justify-center space-y-6">
      <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      <p className="text-slate-500 font-black text-[10px] uppercase tracking-[0.3em]">Loading Experience Engine...</p>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-20">
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-slate-900/40 backdrop-blur-3xl p-8 rounded-[2.5rem] border border-white/5">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/20">
              <SafeIcon icon={FiLayout} />
            </div>
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Experience Designer</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tighter uppercase leading-none">Student Dashboard</h1>
        </div>
        
        <div className="flex items-center gap-4 bg-black/20 p-2 rounded-2xl border border-white/5">
          <button 
            onClick={() => setPreviewMode('desktop')}
            className={`p-3 rounded-xl transition-all ${previewMode === 'desktop' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-500 hover:text-white'}`}
          >
            <SafeIcon icon={FiMonitor} />
          </button>
          <button 
            onClick={() => setPreviewMode('mobile')}
            className={`p-3 rounded-xl transition-all ${previewMode === 'mobile' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-500 hover:text-white'}`}
          >
            <SafeIcon icon={FiSmartphone} />
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2"
          >
            {saving ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
            Publish Experience
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar Controls */}
        <div className="lg:col-span-4 space-y-6">
          {/* Theme Settings */}
          <section className="bg-white/[0.02] border border-white/10 p-8 rounded-[2rem] space-y-6">
            <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-3">
              <SafeIcon icon={FiDroplet} className="text-indigo-400" /> Identity & Theme
            </h3>
            
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Primary Brand Color</label>
              <div className="flex flex-wrap gap-3">
                {['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6'].map(color => (
                  <button 
                    key={color}
                    onClick={() => setConfig({ ...config, theme_config: { ...config.theme_config, primaryColor: color }})}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${config.theme_config.primaryColor === color ? 'border-white scale-110 shadow-lg' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Dashboard Greeting</label>
              <input 
                value={config.welcome_message}
                onChange={(e) => setConfig({ ...config, welcome_message: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white font-medium outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
          </section>

          {/* Widget Architecture */}
          <section className="bg-white/[0.02] border border-white/10 p-8 rounded-[2.5rem] space-y-8">
            <div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-3 mb-2">
                <SafeIcon icon={FiBox} className="text-indigo-400" /> Architecture
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">Reorder and toggle widgets to craft the student flow.</p>
            </div>

            <Reorder.Group 
              axis="y" 
              values={config.layout_schema} 
              onReorder={(newOrder) => setConfig({ ...config, layout_schema: newOrder })}
              className="space-y-3"
            >
              {config.layout_schema.map((widget) => {
                const template = WIDGET_TEMPLATES.find(t => t.id === widget.id);
                return (
                  <Reorder.Item 
                    key={widget.id} 
                    value={widget}
                    className={`p-4 rounded-2xl border transition-all cursor-grab active:cursor-grabbing flex items-center justify-between ${widget.visible ? 'bg-white/5 border-white/10' : 'bg-black/20 border-white/5 opacity-50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-slate-600">
                        <SafeIcon icon={FiMove} />
                      </div>
                      <div>
                        <p className="text-[11px] font-black text-white uppercase tracking-tighter">{template?.label}</p>
                        <p className="text-[9px] text-slate-500 font-bold uppercase">{widget.type}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => toggleWidget(widget.id)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${widget.visible ? 'bg-indigo-500/20 text-indigo-400' : 'bg-white/5 text-slate-600'}`}
                    >
                      <SafeIcon icon={widget.visible ? FiEye : FiTrash2} />
                    </button>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </section>
        </div>

        {/* Live Preview Canvas */}
        <div className="lg:col-span-8">
          <div className={`mx-auto bg-slate-900 border border-white/10 rounded-[3rem] overflow-hidden shadow-2xl transition-all duration-500 ${previewMode === 'mobile' ? 'max-w-[400px] aspect-[9/19]' : 'w-full min-h-[800px]'}`}>
            {/* Top Bar Mockup */}
            <div className="h-20 bg-black/40 border-b border-white/5 flex items-center justify-between px-10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500" style={{ backgroundColor: config.theme_config.primaryColor }} />
                <span className="text-xs font-black text-white uppercase tracking-widest">StudentHub</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 border border-white/10" />
            </div>

            {/* Dashboard Content Mockup */}
            <div className="p-8 space-y-8 overflow-y-auto max-h-[700px] custom-scrollbar">
              <AnimatePresence>
                {config.layout_schema.filter(w => w.visible).map((widget, idx) => (
                  <motion.div
                    key={widget.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="space-y-4"
                  >
                    {widget.id === 'welcome' && (
                      <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 p-10 rounded-[2.5rem] border border-white/10 relative overflow-hidden">
                        <div className="relative z-10">
                          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Good morning, Student</p>
                          <h2 className="text-3xl font-black text-white tracking-tighter leading-none">{config.welcome_message}</h2>
                        </div>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 blur-3xl rounded-full" />
                      </div>
                    )}

                    {widget.id === 'progress' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/[0.02] border border-white/10 p-6 rounded-[2rem]">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Overall Progress</p>
                          <p className="text-2xl font-black text-white">68%</p>
                          <div className="w-full h-1 bg-white/5 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: '68%', backgroundColor: config.theme_config.primaryColor }} />
                          </div>
                        </div>
                        <div className="bg-white/[0.02] border border-white/10 p-6 rounded-[2rem]">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Badges Earned</p>
                          <p className="text-2xl font-black text-white">12</p>
                          <div className="flex gap-1 mt-3">
                            {[1, 2, 3].map(i => <div key={i} className="w-4 h-4 rounded-full bg-amber-500/20 border border-amber-500/40" />)}
                          </div>
                        </div>
                      </div>
                    )}

                    {widget.id === 'resume' && (
                      <div className="bg-white/5 border border-white/10 p-6 rounded-[2rem] flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400" style={{ color: config.theme_config.primaryColor }}>
                            <SafeIcon icon={FiZap} />
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Resume Learning</p>
                            <p className="text-sm font-black text-white">Module 4: Market Dynamics</p>
                          </div>
                        </div>
                        <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white">
                          <SafeIcon icon={FiIcons.FiPlay} />
                        </div>
                      </div>
                    )}

                    {widget.id === 'courses' && (
                      <div className="space-y-4">
                        <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">My Academy</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[1, 2].map(i => (
                            <div key={i} className="bg-black/20 border border-white/5 rounded-[2rem] overflow-hidden group">
                              <div className="h-32 bg-slate-800" />
                              <div className="p-5">
                                <p className="text-xs font-black text-white mb-1">Masterclass 0{i}</p>
                                <p className="text-[9px] text-slate-500 font-bold uppercase">12 Lessons • 4.5 Hours</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Status */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-10 py-5 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Live Preview Pipeline Active</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiCheck} className="text-emerald-400" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Auto-Scale Responsive</span>
        </div>
      </motion.div>
    </div>
  );
}